define("shared/utils/plan-card/tables", ["exports"], function (_exports) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.VALUE_SENTENCE_KEY = _exports.Q3_GOAL_KEY = _exports.Q2_RANGE_KEY = _exports.Q1_PHRASE_ONE_KEY = _exports.Q1_PHRASE_KEY = _exports.PLAN_NAME_KEY = _exports.OFFERS_LABEL_KEY = _exports.HEADLINE_KEY = _exports.FOOTNOTE_KEYS = _exports.DESCRIPTION = _exports.CTA_KEY = _exports.CALLOUT_TAG_KEY = _exports.CALLOUT_ORDER = _exports.CALLOUT_COPY_KEY = _exports.ANSWERED_SET = _exports.ALTERNATIVE_PROMPT_KEY = _exports.ALTERNATIVE = void 0;
  0; //eaimeta@70e063a35619d71feaimeta@70e063a35619d71f
  /**
   * Card copy, transcribed 1:1 from `plan-card-spec.md` so the spec stays diffable against the code.
   * Every value is a translation key, so a copy change is a change to
   * `translations/partials/onboarding/en-us.json`, not to this file.
   */

  const HEADLINE_KEY = _exports.HEADLINE_KEY = {
    q1: 'onboarding.plan_card.headline.default',
    q2: 'onboarding.plan_card.headline.default',
    q3: 'onboarding.plan_card.headline.default',
    q1_q2: 'onboarding.plan_card.headline.default',
    q1_q3: 'onboarding.plan_card.headline.default',
    q2_q3: 'onboarding.plan_card.headline.default',
    q1_q2_q3: 'onboarding.plan_card.headline.default',
    none: 'onboarding.plan_card.headline.zero_answer'
  };

  /**
   * Description rows (§2). Whole templates rather than concatenation — the joining words differ
   * (`across` vs `and want to`) in ways concatenation gets wrong.
   *
   * The zero-answer row is a dedicated line (ticket 14 option (c), Figma `33745-31218`); it cites
   * nothing about the user, so it is the one row that takes no value sentence.
   */
  const DESCRIPTION = _exports.DESCRIPTION = {
    q1: {
      key: 'onboarding.plan_card.description.q1',
      usesValueSentence: true
    },
    q2: {
      key: 'onboarding.plan_card.description.q2',
      usesValueSentence: true
    },
    q3: {
      key: 'onboarding.plan_card.description.q3',
      usesValueSentence: true
    },
    q1_q2: {
      key: 'onboarding.plan_card.description.q1_q2',
      usesValueSentence: true
    },
    q1_q3: {
      key: 'onboarding.plan_card.description.q1_q3',
      usesValueSentence: true
    },
    q2_q3: {
      key: 'onboarding.plan_card.description.q2_q3',
      usesValueSentence: true
    },
    q1_q2_q3: {
      key: 'onboarding.plan_card.description.q1_q2_q3',
      usesValueSentence: true
    },
    none: {
      key: 'onboarding.plan_card.description.none',
      usesValueSentence: false
    }
  };

  /**
   * Q1 answer -> `{q1Phrase}` (§3). Separate keys from the option labels the plan recommendation shows,
   * even where English makes them identical — a string picked standalone and a string embedded
   * mid-sentence inflect differently in other languages.
   */
  const Q1_PHRASE_KEY = _exports.Q1_PHRASE_KEY = {
    own_business: 'onboarding.plan_card.q1_phrase.own_brand',
    in_house_team: 'onboarding.plan_card.q1_phrase.multiple_brands',
    freelance_consultant: 'onboarding.plan_card.q1_phrase.multiple_clients',
    agency: 'onboarding.plan_card.q1_phrase.multiple_clients',
    other: 'onboarding.plan_card.q1_phrase.multiple_profiles'
  };

  /**
   * Q1 answer -> `{q1Phrase}` when Q2 is the single-profile band. Every phrase above that says
   * *"multiple …"* contradicts the range it is joined to once the count is one — *"You manage multiple
   * profiles across 1 profile"* — so that band reads its own row instead.
   */
  const Q1_PHRASE_ONE_KEY = _exports.Q1_PHRASE_ONE_KEY = {
    own_business: 'onboarding.plan_card.q1_phrase.own_brand',
    in_house_team: 'onboarding.plan_card.q1_phrase.your_brand',
    freelance_consultant: 'onboarding.plan_card.q1_phrase.one_client',
    agency: 'onboarding.plan_card.q1_phrase.one_client',
    other: 'onboarding.plan_card.q1_phrase.your_social_presence'
  };

  /**
   * Q2 band -> `{q2Range}` (§3). `21_50` and `51_plus` **deliberately** merge to *"21+ profiles"* —
   * not a duplicate to collapse. Both bands base to Scale, the top tier, and `max()` never lowers a
   * term, so the merged string can only ever appear on a Scale card.
   *
   * The bottom three bands echo their option labels exactly — do not reintroduce the three-range
   * grouping from the example cards. *"1–5 profiles"* matches no band and is hand-written drift (§9).
   */
  const Q2_RANGE_KEY = _exports.Q2_RANGE_KEY = {
    '1': 'onboarding.plan_card.q2_range.one',
    '2_5': 'onboarding.plan_card.q2_range.two_to_five',
    '6_20': 'onboarding.plan_card.q2_range.six_to_twenty',
    '21_50': 'onboarding.plan_card.q2_range.twenty_one_plus',
    '51_plus': 'onboarding.plan_card.q2_range.twenty_one_plus'
  };

  /** Q3 answer -> `{q3Goal}` (§3), lowercased for use after *"and want to"*. */
  const Q3_GOAL_KEY = _exports.Q3_GOAL_KEY = {
    plan_publish: 'onboarding.plan_card.q3_goal.plan_publish',
    collaborate_approve: 'onboarding.plan_card.q3_goal.collaborate_approve',
    respond_messages: 'onboarding.plan_card.q3_goal.respond_messages',
    measure_report: 'onboarding.plan_card.q3_goal.measure_report',
    compare_competitors: 'onboarding.plan_card.q3_goal.compare_competitors'
  };

  /**
   * Plan -> `{planValueSentence}` (§3). Takes the plan name as a param rather than spelling it out:
   * `plans.plan_names.*` is translated in some locales and not others, so a literal here would
   * disagree with the headline in exactly those.
   */
  const VALUE_SENTENCE_KEY = _exports.VALUE_SENTENCE_KEY = {
    starter: 'onboarding.plan_card.value_sentence.starter',
    growth: 'onboarding.plan_card.value_sentence.growth',
    scale: 'onboarding.plan_card.value_sentence.scale'
  };
  const OFFERS_LABEL_KEY = _exports.OFFERS_LABEL_KEY = 'onboarding.plan_card.offers_label';
  const CALLOUT_TAG_KEY = _exports.CALLOUT_TAG_KEY = {
    plan: 'onboarding.plan_card.callout_tag.plan',
    publish: 'onboarding.plan_card.callout_tag.publish',
    approve: 'onboarding.plan_card.callout_tag.approve',
    engage: 'onboarding.plan_card.callout_tag.engage',
    analyze: 'onboarding.plan_card.callout_tag.analyze',
    benchmark: 'onboarding.plan_card.callout_tag.benchmark',
    monitor: 'onboarding.plan_card.callout_tag.monitor'
  };

  /**
   * Callout copy per plan (§4). **Kept per-plan on purpose** — ANALYZE says something different on
   * all three cards, so a shared table would be wrong; splitting a shared key is how a plan's copy
   * diverges later.
   *
   * Partial, because a tier only holds the tags its plan offers. `CALLOUT_ORDER` never names a tag
   * its tier lacks, and the exhaustive card-building tests prove it.
   */
  const CALLOUT_COPY_KEY = _exports.CALLOUT_COPY_KEY = {
    starter: {
      plan: 'onboarding.plan_card.callout_copy.plan',
      publish: 'onboarding.plan_card.callout_copy.publish',
      analyze: 'onboarding.plan_card.callout_copy.analyze_starter'
    },
    growth: {
      plan: 'onboarding.plan_card.callout_copy.plan',
      publish: 'onboarding.plan_card.callout_copy.publish',
      approve: 'onboarding.plan_card.callout_copy.approve',
      engage: 'onboarding.plan_card.callout_copy.engage',
      analyze: 'onboarding.plan_card.callout_copy.analyze_growth'
    },
    scale: {
      plan: 'onboarding.plan_card.callout_copy.plan',
      publish: 'onboarding.plan_card.callout_copy.publish',
      approve: 'onboarding.plan_card.callout_copy.approve',
      engage: 'onboarding.plan_card.callout_copy.engage',
      analyze: 'onboarding.plan_card.callout_copy.analyze_scale',
      benchmark: 'onboarding.plan_card.callout_copy.benchmark',
      monitor: 'onboarding.plan_card.callout_copy.monitor'
    }
  };
  /**
   * Callout selection and ordering (§5). **Order is part of the spec** — the answered goal leads.
   *
   * Starter carries rows only for the two goals reachable on it; the other three floor to Growth or
   * Scale (`plan-recommendation.md` §1), so they can never pair with a Starter tier.
   */
  const CALLOUT_ORDER = _exports.CALLOUT_ORDER = {
    starter: {
      plan_publish: ['plan', 'publish', 'analyze'],
      measure_report: ['analyze', 'plan', 'publish'],
      not_answered: ['plan', 'publish', 'analyze']
    },
    growth: {
      plan_publish: ['plan', 'publish', 'analyze'],
      collaborate_approve: ['approve', 'engage', 'analyze'],
      respond_messages: ['engage', 'approve', 'analyze'],
      measure_report: ['analyze', 'engage', 'approve'],
      not_answered: ['engage', 'approve', 'analyze']
    },
    scale: {
      plan_publish: ['plan', 'publish', 'benchmark'],
      collaborate_approve: ['approve', 'benchmark', 'analyze'],
      respond_messages: ['engage', 'benchmark', 'analyze'],
      measure_report: ['analyze', 'benchmark', 'monitor'],
      compare_competitors: ['benchmark', 'analyze', 'monitor'],
      not_answered: ['benchmark', 'analyze', 'monitor']
    }
  };
  /**
   * Footnote bullets (§6): social sets, users, posts — in that order, per Figma `34232-154691`,
   * `34232-154688`, `34230-154651`. The intro and the after-trial line are the same on every card,
   * so the template holds their keys directly.
   */
  const FOOTNOTE_KEYS = _exports.FOOTNOTE_KEYS = {
    starter: ['onboarding.plan_card.footnote.starter.social_sets', 'onboarding.plan_card.footnote.starter.users', 'onboarding.plan_card.footnote.starter.posts'],
    growth: ['onboarding.plan_card.footnote.growth.social_sets', 'onboarding.plan_card.footnote.growth.users', 'onboarding.plan_card.footnote.growth.posts'],
    scale: ['onboarding.plan_card.footnote.scale.social_sets', 'onboarding.plan_card.footnote.scale.users', 'onboarding.plan_card.footnote.scale.posts']
  };
  const ALTERNATIVE_PROMPT_KEY = _exports.ALTERNATIVE_PROMPT_KEY = 'onboarding.plan_card.alternative.prompt';

  /**
   * The "choose … instead" row (§7). Static per plan and unaffected by the answers. The tier named
   * here is the **next one up**, not the recommended one — the only place on the card where the plan
   * name is not the card's own.
   */
  const ALTERNATIVE = _exports.ALTERNATIVE = {
    starter: {
      tier: 'growth',
      conditionKey: 'onboarding.plan_card.alternative.condition.starter'
    },
    growth: {
      tier: 'scale',
      conditionKey: 'onboarding.plan_card.alternative.condition.growth'
    },
    scale: undefined
  };

  /**
   * The CTA (§1). Both rendered cards read *"Start Growth Free Trial"*, against the spec's stale
   * fixed `Start 14-day free trial →`. One string today; the per-tier row is the seam that makes a
   * future divergence a value change.
   */
  const CTA_KEY = _exports.CTA_KEY = {
    starter: 'onboarding.plan_card.cta',
    growth: 'onboarding.plan_card.cta',
    scale: 'onboarding.plan_card.cta'
  };

  /** Reuses the host app's plan names rather than restating them (`app/services/plans.ts`). */
  const PLAN_NAME_KEY = _exports.PLAN_NAME_KEY = {
    starter: 'plans.plan_names.starter202506',
    growth: 'plans.plan_names.growth202506',
    scale: 'plans.plan_names.scale202506'
  };

  /** Which description row each combination of present answers selects (§2). */
  const ANSWERED_SET = _exports.ANSWERED_SET = {
    true_true_true: 'q1_q2_q3',
    true_true_false: 'q1_q2',
    true_false_true: 'q1_q3',
    true_false_false: 'q1',
    false_true_true: 'q2_q3',
    false_true_false: 'q2',
    false_false_true: 'q3',
    false_false_false: 'none'
  };
});